<template>
  <div
    class="batches-filter"
    @keyup.enter="submitSearch"
  >
    <card title="Filtros">
      <div class="batch-filter-controls mb-5">
        <search-field-selector
          class="batch-filter-period"
          :limit-date="limitDate"
          :date-range-value="dateSelected"
          :default-start-date="dateSelected && dateSelected.start"
          :default-end-date="dateSelected && dateSelected.end"
          :options="dateSelectOptions"
          :emit-initial-value="false"
          @updateSearchValue="setDate"
          @enterPressed="submitSearch"
        />

        <field-container
          v-if="isAllowedCollectionPoint"
          class="batch-filter-collection-point"
          :title="collectionPoint && collectionPoint.value"
          label="Posto de coleta"
        >
          <select-search
            id="txtCollectionPointName"
            ref="refSearchCollectionPoint"
            :value="collectionPoint"
            :options="collectionPointLabels"
            placeholder="Selecione um posto de coleta"
            :uppercase-all-items="true"
            :footer-enabled="false"
            :no-results-message-enabled="true"
            :icon-visible="false"
            no-results-text="Não foi encontrado posto de coleta cadastrado com esse nome"
            @change="setCollectionPoint"
          />
        </field-container>

        <md-button
          class="btn btn-nonenary batch-filter-submit"
          @click="submitSearch"
        >
          <svg-loader src="/static/images/icon-generate-list.svg" />

          Listar
        </md-button>
      </div>

      <batch-packaging-select
        v-model="packagingSelected"
        class="mb-2"
      />

      <div
        :class="{
          'more-filters-open': isMoreOptionsVisible,
        }"
      >
        <md-button
          class="more-filters-button"
          @click="handleToggleMoreFilters"
        >
          Mais filtros

          <md-icon
            md-src="/static/images/arrow-up-primary-color.svg"
            class="more-filters-button-icon"
          />
        </md-button>

        <transition name="dropdown">
          <div v-show="isMoreOptionsVisible">
            <div>
              <p class="mb-2">Origem da OS:</p>

              <md-layout class="gap-4">
                <md-radio
                  v-for="(option, index) in osOriginOptions"
                  :key="index"
                  v-model="osSource"
                  :md-value="option.value"
                  class="mb-0"
                >
                  {{ option.label }}
                </md-radio>
              </md-layout>
            </div>
          </div>
        </transition>
      </div>
    </card>

    <label-data-title
      label="Listagem de lotes"
      :data="currentFilterLabel"
      class="mt-5"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import Cookies from 'js-cookie'
import notify from '@/api/notification'

import { formatDate } from '@/utils/date-utils'
import { arePrintFiltersEqual, persistPrintFilters, restorePrintFilters } from '@/utils/print-filter-session'
import { logCollectionPointError } from '@/utils/collection-point-error'
import {
  BATCH_PACKAGING_FROZEN_ID,
  BATCH_PACKAGING_REFRIGERATED_DRY_ID,
  BATCH_PACKAGING_REFRIGERATED_ID,
  BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
} from '@/enums'

import Card from '@/components/common/Card'
import ButtonIcon from '@/components/common/ButtonIcon'
import SearchFieldSelector from '@/components/common/SearchFieldSelector'
import FieldContainer from '@/components/common/FieldContainer'
import SelectSearch from '@/components/common/SelectSearch'
import BatchPackagingSelect from '@/components/common/BatchPackagingSelect'
import LabelDataTitle from '@/components/common/LabelDataTitle'

function collectionPointName(point) {
  if (!point || typeof point !== 'object' || Array.isArray(point)) return undefined
  return [point.name, point.value, point.label].find(value => typeof value === 'string' && value)
}

export default {
  name: 'BatchesFilter',
  components: {
    Card,
    ButtonIcon,
    SearchFieldSelector,
    FieldContainer,
    SelectSearch,
    BatchPackagingSelect,
    LabelDataTitle,
  },
  data() {
    return {
      refSearchCollectionPoint: null,
      today: new Date(),
      dateSelected: null,
      activeFilter: null,
      packagingSelected: [
        BATCH_PACKAGING_FROZEN_ID,
        BATCH_PACKAGING_REFRIGERATED_ID,
        BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
        BATCH_PACKAGING_REFRIGERATED_DRY_ID,
      ],
      collectionPoint: null,
      hasRestoredCollectionPoint: false,
      integratedRoot: '',
      filtersInitialized: false,
      showModalCollectionPointModal: false,
      limitDaysBatches: 10,
      dateSelectOptions: [
        {
          itemValue: 'TD',
          value: 'transferDate',
          label: 'Período',
          placeholder: 'Período',
          limitDays: 1827,
          type: 'date',
        },
      ],
      osSource: '',
      isMoreOptionsVisible: false,
      osOriginOptions: [
        {
          value: '',
          label: 'Todas',
        },
        {
          value: 'AOL2',
          label: 'AOL',
        },
        {
          value: 'AOLWS',
          label: 'LIS',
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['collectionPointLabels', 'toggle', 'batchesAmount']),
    currentFilterLabel() {
      const formattedStartDate = moment(
        this.activeFilter?.dateSelected?.start ?? this.today
      ).format('DD/MM/YYYY')
      const formattedEndDate = moment(
        this.activeFilter?.dateSelected?.end ?? this.today
      ).format('DD/MM/YYYY')

      let formattedDate =
        formattedStartDate === formattedEndDate
          ? formattedStartDate
          : `${formattedStartDate} até ${formattedEndDate}`

      if (formattedDate === moment(this.today).format('DD/MM/YYYY')) {
        formattedDate = 'hoje'
      }

      return `de ${formattedDate} (${this.batchesAmount})`
    },
    isAllowedCollectionPoint() {
      const collectPlace = this.toggle?.collectPlace
      return collectPlace && this.collectionPointLabels?.length > 0
    },
    startDate() {
      return formatDate(this.dateSelected?.start, 4)
    },
    endDate() {
      return formatDate(this.dateSelected?.end, 4)
    },
    limitDate() {
      return moment(this.today, 'DD/MM/YYYY')
        .subtract(this.limitDaysBatches, 'days')
        .toDate()
    },
    batchFilter() {
      return {
        startDate: this.startDate,
        endDate: this.endDate,
        packagingType: this.packagingSelected,
        integrated: this.integratedRoot,
        collectionPointName: this.collectionPoint?.id === 'ALL' ? undefined : collectionPointName(this.collectionPoint),
        source: this.osSource,
      }
    },
    sessionFilter() {
      return {
        ...this.batchFilter,
        packagingType: [...this.packagingSelected],
        collectionPointName: this.batchFilter.collectionPointName || '',
        showMoreFilters: this.isMoreOptionsVisible,
      }
    },
  },
  watch: {
    '$route.query': {
      handler(query) {
        return this.restoreQueryFilters(query)
      },
    },
    '$route.query.reload': {
      handler(value) {
        if (value) {
          this.submitSearch()

          this.$router.replace({
            query: {
              ...this.$route.query,
              reload: undefined,
            },
          })
        }
      },
    },
    packagingSelected() {
      if (!this.filtersInitialized) return
      this.submitSearch()
    },
    osSource() {
      if (!this.filtersInitialized) return
      this.submitSearch()
    },
  },
  created() {
    this.setInitialFilters()
    this.getSelectedCollectionPoint()
  },
  async mounted() {
    const collectionPointsLoaded = this.loadCollectionPoints()
    await this.$nextTick()
    this.filtersInitialized = true
    await Promise.all([this.submitSearch(), collectionPointsLoaded])
  },
  methods: {
    ...mapActions(['selectBatchList', 'batchGetBatches', 'getAllCollectionPointList']),
    async loadCollectionPoints() {
      const path = this.$route.path
      await this.getAllCollectionPointList()
      if (this._isDestroyed || this._isBeingDestroyed || this.$route.path !== path) return
      if (!this.collectionPoint) return
      this.collectionPoint = (this.collectionPointLabels || []).find(
        point => collectionPointName(point) === collectionPointName(this.collectionPoint)
      ) || this.collectionPoint
      this.collectionPoint = { ...this.collectionPoint, label: collectionPointName(this.collectionPoint) }
      if (this.$refs.refSearchCollectionPoint) {
        this.$refs.refSearchCollectionPoint.field = this.collectionPoint
      }
    },
    async restoreQueryFilters(query) {
      if (!/\/print-tag\/batches\/?$/.test(this.$route.path)) return
      if (!this.filtersInitialized && !this.hydratingFilters) return
      if (arePrintFiltersEqual(query, this.lastAppliedQuery || {})) return
      const generation = (this.filterHydrationGeneration || 0) + 1
      this.filterHydrationGeneration = generation
      this.hydratingFilters = true
      this.filtersInitialized = false
      this.setInitialFilters()
      this.getSelectedCollectionPoint()
      await this.$nextTick()
      if (generation !== this.filterHydrationGeneration) return
      this.hydratingFilters = false
      this.filtersInitialized = true
      return this.submitSearch()
    },
    setInitialFilters() {
      const query = restorePrintFilters('batches', this.$route.query)
      const parseDate = value => moment(value, ['DD/MM/YYYY', moment.ISO_8601], true)
      const start = parseDate(query.startDate)
      const end = parseDate(query.endDate)
      this.dateSelected = {
        start: query.startDate === undefined ? this.today : start.isValid() ? start.toDate() : null,
        end: query.endDate === undefined ? this.today : end.isValid() ? end.toDate() : null,
      }
      if (query.startDate === '' && query.endDate === '') {
        this.dateSelected = { start: null, end: null }
      }
      this.packagingSelected = [
        BATCH_PACKAGING_FROZEN_ID,
        BATCH_PACKAGING_REFRIGERATED_ID,
        BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
        BATCH_PACKAGING_REFRIGERATED_DRY_ID,
      ]
      if (query.packagingType !== undefined) {
        this.packagingSelected = (Array.isArray(query.packagingType)
          ? query.packagingType
          : String(query.packagingType).split(','))
          .filter(value => value !== '')
          .map(Number)
      }
      this.integratedRoot = query.integrated === undefined || query.integrated === ''
        ? ''
        : query.integrated === true || query.integrated === 'true'
      this.osSource = query.source || ''
      this.isMoreOptionsVisible = query.showMoreFilters === true || query.showMoreFilters === 'true'
      this.hasRestoredCollectionPoint = Object.prototype.hasOwnProperty.call(query, 'collectionPointName')
      this.collectionPoint = query.collectionPointName
        ? { label: query.collectionPointName, id: query.collectionPointId }
        : null
    },
    getSelectedCollectionPoint() {
      if (!this.toggle?.collectPlace) return
      if (this.hasRestoredCollectionPoint) {
        this.collectionPoint = (this.collectionPointLabels || []).find(
          point => collectionPointName(point) === collectionPointName(this.collectionPoint)
        ) || this.collectionPoint
        if (this.collectionPoint) {
          this.collectionPoint = { ...this.collectionPoint, label: collectionPointName(this.collectionPoint) }
        }
        if (this.$refs.refSearchCollectionPoint) {
          this.$refs.refSearchCollectionPoint.field = this.collectionPoint
        }
        return
      }

      const savedCollectionPoint = Cookies.get('selectedCollectionPoint')

      if (savedCollectionPoint) {
        try {
          const point = JSON.parse(savedCollectionPoint)
          const name = collectionPointName(point)
          if (!name) throw new Error('Invalid collection point selection')
          this.collectionPoint = { ...point, label: name }
        } catch (error) {
          this.collectionPoint = null
          Cookies.remove('selectedCollectionPoint')
          logCollectionPointError('restore-selection', error)
        }
        if (this.$refs.refSearchCollectionPoint) {
          this.$refs.refSearchCollectionPoint.field = this.collectionPoint
        }
      } else if (this.isAllowedCollectionPoint) {
        this.showModalCollectionPointModal = true
      }
    },
    setDate(e) {
      const date = e?.date
      if (date && !date.start && !date.end) {
        this.dateSelected = { start: null, end: null }
        return
      }
      if (!date?.start || !date?.end) return
      if (!moment(date.start).isValid() || !moment(date.end).isValid()) return
      if (moment(date.end).isBefore(date.start, 'day')) return
      const changed = !moment(date.start).isSame(this.dateSelected?.start, 'day') ||
        !moment(date.end).isSame(this.dateSelected?.end, 'day')
      this.dateSelected = { start: new Date(date.start), end: new Date(date.end) }
      if (changed && this.filtersInitialized) return this.submitSearch()
    },
    setCollectionPoint(event) {
      this.hasRestoredCollectionPoint = true
      // vue-select also emits change when we synchronize its selected value.
      if (event === this.collectionPoint) return
      const name = collectionPointName(event)
      this.collectionPoint = name ? { ...event, label: name } : null
      try {
        if (this.collectionPoint) {
          Cookies.set('selectedCollectionPoint', JSON.stringify(this.collectionPoint), { expires: 7 })
        } else {
          Cookies.remove('selectedCollectionPoint')
        }
      } catch (error) {
        logCollectionPointError('save-selection', error)
      }
      if (this.$refs.refSearchCollectionPoint) this.$refs.refSearchCollectionPoint.field = this.collectionPoint
      if (this.filtersInitialized) return this.submitSearch()
    },
    setCollectionPointModal(event) {
      this.showModalCollectionPointModal = false
      return this.setCollectionPoint(event)
    },
    async submitSearch() {
      const path = this.$route.path
      await this.$nextTick()
      if (this._isDestroyed || this._isBeingDestroyed || this.$route.path !== path) return
      const searchKey = JSON.stringify(this.sessionFilter)
      if (this.searchPromise && this.searchKey === searchKey) return this.searchPromise
      this.searchKey = searchKey
      const generation = (this.searchGeneration || 0) + 1
      this.searchGeneration = generation
      this.lastAppliedQuery = { tab: this.$route.query.tab, ...this.sessionFilter }
      const filters = { ...this.batchFilter, packagingType: [...this.packagingSelected] }
      const search = async () => {
        this.activeFilter = {
          dateSelected: { ...this.dateSelected },
          collectionPoint: this.collectionPoint,
        }
        await this.saveFilters()
        if (this._isDestroyed || this._isBeingDestroyed || this.$route.path !== path || this.searchGeneration !== generation) return
        this.selectBatchList(filters)
        await this.batchGetBatches()
      }
      this.searchPromise = search()
      try {
        await this.searchPromise
      } catch (error) {
        logCollectionPointError('load-batches', error)
        notify({ title: 'AVISO:', message: 'Erro ao buscar lotes. Tente novamente.' })
      } finally {
        if (this.searchKey === searchKey) this.searchPromise = null
      }
    },
    saveFilters() {
      return persistPrintFilters(this, 'batches', this.sessionFilter)
    },
    hintPackagingType(value) {
      const packagingSelected = this.packagingSelected
      const hintSelect = 'Selecione para filtrar uma ou mais formas de acondicionamento'
      const hintDeselect = 'Clique para remover filtro'

      if (packagingSelected?.length === 4 && value) return hintSelect

      if (value) return hintDeselect

      return hintSelect
    },
    handleToggleMoreFilters() {
      this.isMoreOptionsVisible = !this.isMoreOptionsVisible
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;
@use '@/../static/stylesheets/components/batch-filter-controls';

:deep(.search .search-field-selector) {
  height: 40px;
  min-height: auto;
}

:deep(.field) {
  height: 40px !important;
}

.batches-filter {
  .btn {
    height: 40px;
    display: flex;
    justify-items: center;
    gap: 0.5rem;

    .md-icon {
      width: 1.375rem;
      height: 1.375rem;
      min-width: auto;
      min-height: auto;
    }
  }

  .md-radio {
    margin: 0;
  }

  .more-filters {
    &-button {
      margin: 0;
      padding: 0;
      background-color: transparent;
      color: $theme-primary-color;
      min-height: auto;
      height: auto;

      &:hover {
        background-color: transparent;
      }

      &-icon {
        transform: rotate(180deg);
        transition: transform 0.3s ease-in-out;
      }

      .md-icon {
        min-width: auto;
        min-height: auto;
        width: 1rem;
        height: 1rem;
      }

      ::v-deep(.md-ink-ripple) {
        display: none !important;
      }
    }
  }

  .more-filters-open .more-filters-button-icon {
    transform: rotate(0deg) !important;
  }
}

:deep(.md-theme-default.md-radio .md-radio-container:after) {
  background-color: $theme-primary-color !important;
}

:deep(.md-theme-default.md-radio.md-checked .md-radio-container) {
  border-color: $theme-primary-color !important;
}

::v-deep(.card-form__container .title-main) {
  color: $theme-primary-color;
}
</style>
